import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { motion } from 'framer-motion';
import { Package as PackageIcon, Gift, Check } from 'lucide-react';
import Button from '../components/ui/Button';
import { packageService } from '../services/packageService';
import PaymentSection from '../components/payments/PaymentSection';
import { paymentService } from '../services/paymentService';
import { stripePromise } from '../lib/stripe';
import { scrollAndFocus } from '../utils/scroll';
import { usePaymentCleanup } from '../hooks/usePaymentCleanup';
import SEOHead from '../components/SEOHead';
import type { Package, PackagePurchase } from '../types/package';

const packagePurchaseSchema = z.object({
  package_id: z.string().min(1, "Veuillez choisir un forfait"),
  purchaser_firstname: z.string().min(2, "Le prénom doit contenir au moins 2 caractères"),
  purchaser_name: z.string().min(2, "Le nom doit contenir au moins 2 caractères"),
  purchaser_email: z.string().email("Adresse email invalide"),
  purchaser_phone: z.string().regex(/^(?:(?:\+|00)33|0)\s*[1-9](?:[\s.-]*\d{2}){4}$/, "Numéro de téléphone invalide").optional().or(z.literal("")),
  purchaser_address_line1: z.string().min(1, "L'adresse est obligatoire"),
  purchaser_address_line2: z.string().optional(),
  purchaser_postal_code: z.string().regex(/^\d{5}$/, "Code postal invalide (5 chiffres)"),
  purchaser_city: z.string().min(1, "La ville est obligatoire"),
  purchaser_country: z.literal("France"),
  recipient_firstname: z.string().optional(),
  recipient_lastname: z.string().optional(),
  personal_message: z.string().max(500, "Le message ne peut pas dépasser 500 caractères").optional(),
});

type PackagePurchaseForm = z.infer<typeof packagePurchaseSchema>;

const Packages: React.FC = () => {
  // Synchronously check for post-payment redirect to prevent UI flash
  const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
  const isPostPaymentRedirect = params.has('payment_intent_client_secret') && params.has('package_purchase_id');
  
  const [packages, setPackages] = useState<Package[]>([]);
  const [loadingPackages, setLoadingPackages] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createdPurchase, setCreatedPurchase] = useState<PackagePurchase | null>(null);
  const [paymentClientSecret, setPaymentClientSecret] = useState<string | null>(null);
  const [isConfirmingPayment, setIsConfirmingPayment] = useState<boolean>(isPostPaymentRedirect);
  const [paymentError, setPaymentError] = useState<string | null>(() => {
    const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
    if (params.has('payment_failed')) {
      return 'Le paiement a échoué. Veuillez réessayer ou utiliser un autre moyen de paiement.';
    }
    return null;
  });
  const [ariaMessage, setAriaMessage] = useState('');

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors }
  } = useForm<PackagePurchaseForm>({
    resolver: zodResolver(packagePurchaseSchema),
  });

  const selectedPackageId = watch('package_id');

  // Extraire le PaymentIntent ID du clientSecret
  const paymentIntentId = paymentClientSecret ? paymentClientSecret.split('_secret_')[0] : null;
  
  // Hook pour annuler automatiquement le PaymentIntent si l'utilisateur quitte la page
  // Désactivé pendant la confirmation post-redirection pour éviter l'annulation
  usePaymentCleanup({
    paymentIntentId,
    isPaymentComplete: createdPurchase?.payment_status === 'paid' || isConfirmingPayment,
    enabled: !!paymentClientSecret && createdPurchase?.payment_status !== 'paid' && !isPostPaymentRedirect,
  });

  // Charger les forfaits disponibles
  useEffect(() => {
    const loadPackages = async () => {
      try {
        const data = await packageService.getAvailablePackages();
        setPackages(data);
      } catch (error) {
        console.error('Erreur chargement forfaits:', error);
      } finally {
        setLoadingPackages(false);
      }
    };
    loadPackages();
  }, []);

  // When a package is selected, scroll to buyer info
  useEffect(() => {
    if (!selectedPackageId) return;
    const tryScroll = () => {
      const buyer = document.querySelector('[data-section="buyer"]') as HTMLElement | null;
      if (buyer) {
        scrollAndFocus(buyer, { hash: '#acheteur', announce: setAriaMessage, message: 'Étape 2 : Vos informations', focusSelector: 'h3, input', pushState: true });
      } else {
        requestAnimationFrame(tryScroll);
      }
    };
    setTimeout(() => requestAnimationFrame(tryScroll), 100);
  }, [selectedPackageId]);

  // Confirmer le paiement après redirection Stripe
  useEffect(() => {
    if (!isPostPaymentRedirect) return;

    const confirmPaymentAfterRedirect = async () => {
      const params = new URLSearchParams(window.location.search);
      const clientSecret = params.get('payment_intent_client_secret');
      const packagePurchaseId = params.get('package_purchase_id');

      if (!clientSecret || !packagePurchaseId) {
        setPaymentError('Paramètres de paiement manquants');
        setIsConfirmingPayment(false);
        return;
      }

      try {
        const stripe = await stripePromise;
        if (!stripe) throw new Error('Stripe non initialisé');

        const { error, paymentIntent } = await stripe.retrievePaymentIntent(clientSecret);

        if (error) {
          setPaymentError(error.message || 'Erreur lors de la confirmation du paiement');
          setIsConfirmingPayment(false);
          return;
        }

        if (paymentIntent?.status === 'succeeded') {
          // Confirme le paiement côté serveur (génération des bons + emails), sans
          // dépendre du webhook Stripe. Idempotent si le webhook est passé avant.
          try {
            await packageService.markPaid(parseInt(packagePurchaseId), paymentIntent.id);
          } catch (err) {
            console.error('Erreur de confirmation paiement forfait:', err);
          }

          // Retry pour laisser le temps aux bons d'apparaître
          let retries = 0;
          const maxRetries = 5;
          const retryDelay = 1000; // 1 seconde
          
          const fetchPurchaseWithRetry = async (): Promise<void> => {
            try {
              const purchase = await packageService.getPurchase(parseInt(packagePurchaseId));
              
              // Si le purchase n'a pas encore de vouchers et qu'on peut retry, attendre
              if (purchase.vouchers.length === 0 && retries < maxRetries) {
                retries++;
                await new Promise(resolve => setTimeout(resolve, retryDelay));
                return fetchPurchaseWithRetry();
              }
              
              setCreatedPurchase(purchase);
              
              // Nettoyer l'URL
              window.history.replaceState({}, '', window.location.pathname);
              
              // Scroll vers la confirmation
              setTimeout(() => {
                const el = document.querySelector('[data-section="confirmation"]') as HTMLElement | null;
                if (el) {
                  scrollAndFocus(el, { hash: '#confirmation', announce: setAriaMessage, message: 'Paiement confirmé', focusSelector: 'h2', pushState: false });
                }
              }, 100);
            } catch (error) {
              if (retries < maxRetries) {
                retries++;
                await new Promise(resolve => setTimeout(resolve, retryDelay));
                return fetchPurchaseWithRetry();
              }
              throw error;
            }
          };
          
          await fetchPurchaseWithRetry();
        } else {
          setPaymentError('Le paiement n\'a pas été confirmé');
        }
      } catch (error) {
        console.error('Erreur confirmation paiement:', error);
        setPaymentError('Erreur lors de la confirmation du paiement');
      } finally {
        setIsConfirmingPayment(false);
      }
    };

    confirmPaymentAfterRedirect();
  }, [isPostPaymentRedirect]);

  const onSubmit = async (data: PackagePurchaseForm) => {
    setIsSubmitting(true);
    setPaymentError(null);

    try {
      // 1. Créer l'achat de forfait
      const purchase = await packageService.createPurchase({
        package_id: parseInt(data.package_id),
        purchaser_firstname: data.purchaser_firstname,
        purchaser_name: data.purchaser_name,
        purchaser_email: data.purchaser_email,
        purchaser_phone: data.purchaser_phone || undefined,
        purchaser_address_line1: data.purchaser_address_line1,
        purchaser_address_line2: data.purchaser_address_line2 || undefined,
        purchaser_postal_code: data.purchaser_postal_code,
        purchaser_city: data.purchaser_city,
        purchaser_country: data.purchaser_country,
        recipient_firstname: data.recipient_firstname || undefined,
        recipient_lastname: data.recipient_lastname || undefined,
        personal_message: data.personal_message || undefined,
      });

      setCreatedPurchase(purchase);

      // 2. Créer le PaymentIntent
      const paymentIntent = await paymentService.createPaymentIntent({
        type: 'package',
        package_purchase_id: purchase.id,
        amount_cents: purchase.amount_cents,
        customer_email: purchase.purchaser_email,
      });

      setPaymentClientSecret(paymentIntent.client_secret);

      // Scroll vers la section paiement
      setTimeout(() => {
        const el = document.querySelector('[data-section="payment"]') as HTMLElement | null;
        if (el) {
          scrollAndFocus(el, { hash: '#paiement', announce: setAriaMessage, message: 'Étape 3 : Paiement', focusSelector: 'h3', pushState: true });
        }
      }, 100);

    } catch (error: any) {
      console.error('Erreur création achat:', error);
      setPaymentError(error.message || 'Une erreur est survenue lors de la création de l\'achat');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePaymentSuccess = async (paymentIntentId: string) => {
    if (!createdPurchase) return;

    try {
      // Confirme le paiement côté serveur : génère les bons et envoie les emails
      // sans attendre le webhook Stripe. Idempotent si le webhook est passé avant.
      try {
        await packageService.markPaid(createdPurchase.id, paymentIntentId);
      } catch (err) {
        console.error('Erreur de confirmation paiement forfait:', err);
      }
      const updatedPurchase = await packageService.getPurchase(createdPurchase.id);
      setCreatedPurchase(updatedPurchase);

      // Scroll vers la confirmation
      setTimeout(() => {
        const el = document.querySelector('[data-section="confirmation"]') as HTMLElement | null;
        if (el) {
          scrollAndFocus(el, { hash: '#confirmation', announce: setAriaMessage, message: 'Paiement confirmé', focusSelector: 'h2', pushState: true });
        }
      }, 100);
    } catch (error) {
      console.error('Erreur récupération achat:', error);
    }
  };

  const handlePaymentError = (error: string) => {
    setPaymentError(error);
  };

  if (loadingPackages) {
    return (
      <div className="min-h-screen bg-neutral-offwhite py-12">
        <SEOHead
          title="Forfaits Bien-être Périnatal"
          description="Offrez-vous un forfait spa bébé ou massage prénatal à Geneuille près de Besançon. Tarifs et formules bien-être — Doux ce Moman."
          path="/forfaits"
        />
        <h1 className="sr-only">Nos Forfaits</h1>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-600 mx-auto"></div>
            <p className="font-nunito mt-4 text-neutral-body">Chargement des forfaits...</p>
          </div>
        </div>
      </div>
    );
  }

  // Affichage spinner pendant la confirmation du paiement après redirection
  if (isConfirmingPayment && !createdPurchase) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-rose-50 to-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-md mx-auto mt-20">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-rose-600 mx-auto mb-6"></div>
            <p className="font-playfair text-2xl font-semibold text-neutral-ink mb-2">Confirmation du paiement en cours...</p>
            <p className="font-nunito text-neutral-body">Veuillez patienter quelques instants</p>
          </div>
        </div>
      </div>
    );
  }

  // Affichage paiement/confirmation (remplace tout le contenu)
  if (createdPurchase) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-rose-50 to-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Aria live region */}
          <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
            {ariaMessage}
          </div>

          {/* Erreurs de paiement */}
          {paymentError && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg max-w-3xl mx-auto"
            >
              <p className="text-red-800">{paymentError}</p>
            </motion.div>
          )}

          {/* Confirmation paiement en cours */}
          {isConfirmingPayment && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mb-6 p-6 bg-blue-50 border border-blue-200 rounded-lg text-center max-w-3xl mx-auto"
            >
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-blue-800 font-medium">Confirmation du paiement en cours...</p>
            </motion.div>
          )}

          <div className="max-w-3xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl shadow-xl p-8"
              data-section={createdPurchase.payment_status === 'paid' ? 'confirmation' : 'payment'}
            >
              {createdPurchase.payment_status === 'paid' ? (
                // Confirmation de succès
                <div className="text-center space-y-8">
                  <div className="flex flex-col items-center gap-4">
                    <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-brand-light/90 to-brand text-neutral-offwhite shadow-lg shadow-brand/20">
                      <Check className="w-9 h-9" />
                    </div>
                    <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-brand/10 text-brand uppercase tracking-wide text-xs font-semibold">
                      Paiement confirmé
                    </span>
                    <h2 className="text-3xl md:text-4xl font-bold text-neutral-ink outline-none">
                      Forfait acheté avec succès !
                    </h2>
                    <p className="text-lg text-neutral-body max-w-2xl">
                      Nous venons de vous envoyer un email récapitulatif avec l’ensemble de vos bons cadeaux. Vous pouvez les transmettre dès maintenant à vos proches.
                    </p>
                  </div>

                  <div className="grid gap-6 text-left md:grid-cols-[1.3fr_1fr]">
                    <div className="rounded-3xl border border-brand/15 bg-white/90 shadow-soft p-6 md:p-8">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <p className="text-sm font-semibold uppercase tracking-wide text-brand/80 mb-2">
                            Forfait sélectionné
                          </p>
                          <h3 className="text-2xl font-bold text-neutral-ink">
                            {createdPurchase.package.name}
                          </h3>
                          <p className="text-sm text-neutral-body mt-2">
                            Montant total : <span className="font-semibold text-brand">{(createdPurchase.amount_cents / 100).toFixed(2)}€</span>
                          </p>
                        </div>
                        <div className="hidden md:flex flex-col items-end">
                          <span className="inline-flex items-center rounded-full bg-accent-sand px-3 py-1 text-xs font-semibold text-neutral-body">
                            {createdPurchase.vouchers.length} bons générés
                          </span>
                        </div>
                      </div>

                      <div className="mt-6 space-y-3">
                        {createdPurchase.vouchers.map((voucher, index) => (
                          <div
                            key={index}
                            className="flex flex-col gap-3 rounded-2xl border border-brand/10 bg-accent-rose/35 px-4 py-3"
                          >
                            <div className="flex items-center gap-3">
                              <Gift className="w-5 h-5 text-brand flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold text-neutral-ink">{voucher.service_name}</p>
                                <p className="text-xs text-neutral-body/80">Valable 9 mois à compter d’aujourd’hui</p>
                              </div>
                            </div>
                            <div className="flex items-center justify-center">
                              <span className="font-mono text-xs sm:text-sm font-semibold text-brand bg-white/70 px-3 py-1.5 rounded-full border border-brand/20 whitespace-nowrap">
                                {voucher.voucher_number}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="rounded-3xl border border-brand/10 bg-white/80 shadow-soft p-6 md:p-8 space-y-5">
                      <div>
                        <h4 className="text-sm font-semibold uppercase tracking-wide text-brand/80 mb-3">Destinataire</h4>
                        <p className="text-neutral-ink font-semibold">
                          {createdPurchase.recipient_firstname || createdPurchase.purchaser_firstname} {createdPurchase.recipient_lastname || createdPurchase.purchaser_name}
                        </p>
                        <p className="text-sm text-neutral-body mt-1">
                          L’email de confirmation a été envoyé à {createdPurchase.purchaser_email}.
                        </p>
                      </div>

                      <div className="h-px bg-neutral-line" />

                      <div className="space-y-2 text-sm text-neutral-body">
                        <div className="flex items-center justify-between">
                          <span>Date d’achat</span>
                          <span className="font-semibold text-neutral-ink">
                            {new Date(createdPurchase.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })}
                          </span>
                        </div>
                        {createdPurchase.paid_at && (
                          <div className="flex items-center justify-between">
                            <span>Paiement confirmé</span>
                            <span className="font-semibold text-neutral-ink">
                              {new Date(createdPurchase.paid_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                        )}
                      </div>

                      <div className="rounded-2xl bg-accent-rose/40 border border-brand/10 px-4 py-3 text-sm text-neutral-body">
                        Grâce à vos bons cadeaux, vos proches pourront réserver directement en ligne en mentionnant le numéro correspondant.
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-center">
                    <Button onClick={() => window.location.href = '/'}>
                      Retour à l'accueil
                    </Button>
                  </div>
                </div>
              ) : (
                // Section paiement
                <>
                  <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-rose-100 rounded-full mb-4">
                      <PackageIcon className="w-8 h-8 text-rose-600" />
                    </div>
                    <h2 className="font-playfair text-3xl font-bold text-neutral-ink mb-4">
                      Finalisez votre achat
                    </h2>
                    <p className="font-nunito text-lg text-neutral-body">
                      Veuillez procéder au paiement pour activer votre forfait.
                    </p>
                  </div>

                  {/* Récapitulatif */}
                  <div className="bg-rose-50 rounded-xl p-6 mb-6">
                    <h3 className="font-playfair text-xl font-semibold text-neutral-ink mb-4">
                      {createdPurchase.package.name}
                    </h3>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span>Prix :</span>
                        <span className="font-semibold">{(createdPurchase.amount_cents / 100).toFixed(2)}€</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Destinataire :</span>
                        <span>{createdPurchase.recipient_firstname || createdPurchase.purchaser_firstname} {createdPurchase.recipient_lastname || createdPurchase.purchaser_name}</span>
                      </div>
                    </div>
                  </div>

                  {/* Paiement */}
                  {paymentClientSecret && (
                    <PaymentSection
                      clientSecret={paymentClientSecret}
                      amountCents={createdPurchase.amount_cents}
                      returnUrl={typeof window !== 'undefined' ? `${window.location.origin}/forfaits?package_purchase_id=${createdPurchase.id}` : undefined}
                      onSuccess={handlePaymentSuccess}
                      onError={handlePaymentError}
                    />
                  )}
                </>
              )}
            </motion.div>
          </div>
        </div>
      </div>
    );
  }

  // Formulaire principal (sélection forfait + informations)
  return (
    <div className="min-h-screen bg-neutral-offwhite py-12">
      <SEOHead
        title="Forfaits Bien-être Périnatal"
        description="Offrez-vous un forfait spa bébé ou massage prénatal à Geneuille près de Besançon. Tarifs et formules bien-être — Doux ce Moman."
        path="/forfaits"
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center justify-center w-16 h-16 bg-accent-rose rounded-full mb-4">
            <PackageIcon className="w-8 h-8 text-brand" />
          </div>
          <h1 className="font-playfair text-4xl md:text-5xl font-bold text-neutral-ink mb-6">
            Nos Forfaits
          </h1>
          <p className="font-nunito text-neutral-body max-w-2xl mx-auto text-lg md:text-xl leading-relaxed">
            Offrez un moment de détente avec nos forfaits bien-être. Chaque forfait génère plusieurs bons cadeaux utilisables séparément.
          </p>
        </motion.div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
            {/* Étape 1 : Sélection du forfait */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-2xl shadow-lg p-8"
            >
              <div className="flex items-center mb-6">
                <div className="flex items-center justify-center w-10 h-10 bg-rose-100 rounded-full mr-4">
                  <span className="text-rose-600 font-bold">1</span>
                </div>
                <h2 className="font-playfair text-2xl font-bold text-neutral-ink">Choisissez votre forfait</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {packages.map((pkg) => (
                  <label
                    key={pkg.id}
                    className={`relative cursor-pointer rounded-xl border-2 p-6 transition-all ${
                      selectedPackageId === pkg.id.toString()
                        ? 'border-brand bg-accent-rose/40'
                        : 'border-neutral-line hover:border-brand/40'
                    }`}
                  >
                    <input
                      type="radio"
                      value={pkg.id}
                      {...register('package_id')}
                      className="sr-only"
                    />
                    <div className="flex flex-col h-full">
                      <div className="flex items-center justify-between mb-4">
                        <Gift className="w-8 h-8 text-brand" />
                        <span className="text-2xl font-bold text-brand">
                          {(pkg.price_cents / 100).toFixed(0)}€
                        </span>
                      </div>
                      <h3 className="font-playfair text-lg font-bold text-neutral-ink mb-2">
                        {pkg.name}
                      </h3>
                      <p className="font-nunito text-sm text-neutral-body mb-4">
                        {pkg.description}
                      </p>
                      <div className="mt-auto space-y-2">
                        {pkg.items.map((item, index) => (
                          <div key={index} className="flex items-center font-nunito text-sm text-neutral-body">
                            <Check className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                            <span>{item.quantity}x {item.service_name}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
              {errors.package_id && (
                <p className="mt-2 text-sm text-red-600">{errors.package_id.message}</p>
              )}
            </motion.div>

            {/* Étape 2 : Informations acheteur */}
            {selectedPackageId && (
              <motion.div
                data-section="buyer"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-2xl shadow-lg p-8"
              >
                <div className="flex items-center mb-6">
                  <div className="flex items-center justify-center w-10 h-10 bg-rose-100 rounded-full mr-4">
                    <span className="text-rose-600 font-bold">2</span>
                  </div>
                  <h3 className="font-playfair text-2xl font-bold text-neutral-ink">Vos informations</h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block font-nunito text-sm font-medium text-neutral-body mb-2">
                      Prénom
                    </label>
                    <input
                      type="text"
                      {...register('purchaser_firstname')}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                    />
                    {errors.purchaser_firstname && (
                      <p className="mt-1 text-sm text-red-600">{errors.purchaser_firstname.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block font-nunito text-sm font-medium text-neutral-body mb-2">
                      Nom
                    </label>
                    <input
                      type="text"
                      {...register('purchaser_name')}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                    />
                    {errors.purchaser_name && (
                      <p className="mt-1 text-sm text-red-600">{errors.purchaser_name.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block font-nunito text-sm font-medium text-neutral-body mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      {...register('purchaser_email')}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                    />
                    {errors.purchaser_email && (
                      <p className="mt-1 text-sm text-red-600">{errors.purchaser_email.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block font-nunito text-sm font-medium text-neutral-body mb-2">
                      Téléphone (optionnel)
                    </label>
                    <input
                      type="tel"
                      {...register('purchaser_phone')}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                    />
                    {errors.purchaser_phone && (
                      <p className="mt-1 text-sm text-red-600">{errors.purchaser_phone.message}</p>
                    )}
                  </div>
                </div>

                {/* Section Adresse */}
                <div className="mt-8 pt-8 border-t border-gray-200">
                  <h4 className="font-playfair text-lg font-semibold text-neutral-ink mb-4">
                    Votre adresse
                  </h4>
                  <div className="grid grid-cols-1 gap-6">
                    <div>
                      <label className="block font-nunito text-sm font-medium text-neutral-body mb-2">
                        Adresse
                      </label>
                      <input
                        type="text"
                        {...register('purchaser_address_line1')}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                        placeholder="Numéro et nom de rue"
                      />
                      {errors.purchaser_address_line1 && (
                        <p className="mt-1 text-sm text-red-600">{errors.purchaser_address_line1.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block font-nunito text-sm font-medium text-neutral-body mb-2">
                        Complément d'adresse (optionnel)
                      </label>
                      <input
                        type="text"
                        {...register('purchaser_address_line2')}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                        placeholder="Appartement, bâtiment, etc. (optionnel)"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block font-nunito text-sm font-medium text-neutral-body mb-2">
                          Code postal
                        </label>
                        <input
                          type="text"
                          {...register('purchaser_postal_code')}
                          maxLength={5}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                          placeholder="75001"
                        />
                        {errors.purchaser_postal_code && (
                          <p className="mt-1 text-sm text-red-600">{errors.purchaser_postal_code.message}</p>
                        )}
                      </div>

                      <div>
                        <label className="block font-nunito text-sm font-medium text-neutral-body mb-2">
                          Ville
                        </label>
                        <input
                          type="text"
                          {...register('purchaser_city')}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                          placeholder="Paris"
                        />
                        {errors.purchaser_city && (
                          <p className="mt-1 text-sm text-red-600">{errors.purchaser_city.message}</p>
                        )}
                      </div>
                    </div>

                    <input type="hidden" {...register('purchaser_country')} value="France" />
                  </div>
                </div>

                {/* Informations destinataire */}
                <div className="mt-8 pt-8 border-t border-gray-200">
                  <h4 className="font-playfair text-lg font-semibold text-neutral-ink mb-4">
                    Destinataire du forfait (optionnel)
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block font-nunito text-sm font-medium text-neutral-body mb-2">
                        Prénom du destinataire
                      </label>
                      <input
                        type="text"
                        {...register('recipient_firstname')}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block font-nunito text-sm font-medium text-neutral-body mb-2">
                        Nom du destinataire
                      </label>
                      <input
                        type="text"
                        {...register('recipient_lastname')}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div className="mt-6">
                    <label className="block font-nunito text-sm font-medium text-neutral-body mb-2">
                      Message personnel (optionnel)
                    </label>
                    <textarea
                      {...register('personal_message')}
                      rows={4}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                      placeholder="Ajoutez un message personnel pour le destinataire..."
                    />
                    {errors.personal_message && (
                      <p className="mt-1 text-sm text-red-600">{errors.personal_message.message}</p>
                    )}
                  </div>
                </div>

                <div className="mt-8">
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full"
                  >
                    {isSubmitting ? 'Préparation du paiement...' : 'Continuer vers le paiement'}
                  </Button>
                </div>
              </motion.div>
            )}
          </form>
      </div>
    </div>
  );
};

export default Packages;
